import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/cn';

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

/**
 * Brand-colour picker — findings.md #5: a raw `<input type="color">` (an OS
 * dialog behind an unstyled native rectangle) was the one generic form field
 * left in the brand flow. This keeps the native colour dialog as a capability
 * (still the most direct way to pick "roughly this shade") but makes the
 * VISIBLE surface a styled swatch plus a typed hex field — the more likely
 * path anyway, since a fleet usually already knows its brand hex.
 */
export function ColorPicker({
  id,
  value,
  onChange,
  disabled,
}: {
  id: string;
  /** Six-digit hex, e.g. "#0066FF". */
  value: string;
  onChange: (hex: string) => void;
  disabled?: boolean;
}) {
  const nativeRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState(value);
  const [invalid, setInvalid] = useState(false);

  // Keep the typed field in sync when the value changes from elsewhere
  // (a preset swatch, or the native dialog).
  useEffect(() => {
    setDraft(value);
    setInvalid(false);
  }, [value]);

  function commitDraft(raw: string) {
    const hex = raw.trim();
    const normalised = hex.startsWith('#') ? hex : `#${hex}`;
    if (HEX_RE.test(normalised)) {
      setInvalid(false);
      onChange(normalised);
    } else {
      setInvalid(true);
    }
  }

  return (
    <div className="inline-flex items-center gap-2">
      <button
        type="button"
        id={id}
        disabled={disabled}
        onClick={() => nativeRef.current?.click()}
        title="Pick a colour"
        className={cn(
          'h-9 w-9 shrink-0 rounded-[10px] border border-breaker-line-2 transition-all duration-200 ease-out',
          'hover:scale-105 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100',
        )}
        style={{ backgroundColor: HEX_RE.test(value) ? value : undefined }}
      >
        {/* The native input is the picker's actual colour-selection UI —
            visually hidden, invoked only by the swatch button above, never
            shown to the user as a raw generic field. */}
        <input
          ref={nativeRef}
          type="color"
          value={HEX_RE.test(value) ? value : '#000000'}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          className="h-0 w-0 opacity-0"
          tabIndex={-1}
          aria-hidden
        />
      </button>
      <input
        type="text"
        value={draft}
        disabled={disabled}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => commitDraft(draft)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commitDraft(draft);
        }}
        placeholder="#0066FF"
        maxLength={7}
        className={cn(
          'h-9 w-24 rounded-[10px] border bg-white px-2 text-sm font-mono tabular-nums text-breaker-ink outline-none transition-colors',
          'focus-visible:ring-2 focus-visible:ring-breaker-identity',
          invalid ? 'border-breaker-red' : 'border-breaker-line-2',
          'disabled:cursor-not-allowed disabled:bg-breaker-ground-2 disabled:text-breaker-mute',
        )}
      />
    </div>
  );
}
